import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import client from "@/lib/api";
import Loader from "@/components/Loader";
import ShareButtons from "@/components/ShareButtons";
import usePageTitle from "@/hooks/usePageTitle";

export function ThoughtLeadership() {
  const [items, setItems] = useState(null);
  usePageTitle("Thought Leadership", "Essays and notes by Nitin Narang on chess administration, sports governance, AI policy, entrepreneurship and India's growth story.");
  useEffect(() => { client.get("/articles").then(r => setItems(r.data.items)); }, []);

  return (
    <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
      <div className="mb-16">
        <div className="eyebrow mb-4">Thought Leadership</div>
        <h1 className="serif-title text-5xl lg:text-6xl text-[#0a1628] mb-4">Notes from the field.</h1>
        <p className="text-[#4a4a4a] text-[17px] leading-relaxed max-w-3xl">
          Essays and notes on chess administration, sports governance, entrepreneurship, leadership and India's growth story.
        </p>
      </div>

      {!items ? <Loader label="Loading articles" /> : (
        <div className="space-y-6">
          {items.map(a => (
            <Link key={a.slug} to={`/thought-leadership/${a.slug}`}
              data-testid={`article-${a.slug}`}
              className="block border hairline bg-white p-8 lg:p-10 card-hover">
              <div className="grid lg:grid-cols-12 gap-6">
                <div className="lg:col-span-3">
                  <div className="eyebrow mb-2">{a.category}</div>
                  <div className="text-[12px] tracking-[0.18em] uppercase text-[#6b6b6b]">
                    {new Date(a.date).toLocaleDateString("en-IN", {month: "long", day: "numeric", year: "numeric"})}
                  </div>
                </div>
                <div className="lg:col-span-9">
                  <h3 className="font-display text-2xl lg:text-3xl text-[#0a1628] mb-3 leading-snug">{a.title}</h3>
                  <p className="text-[#4a4a4a] text-[15px] leading-relaxed mb-4">{a.excerpt}</p>
                  <span className="link-underline text-[12px] tracking-[0.18em] uppercase font-medium">Read essay →</span>
                </div>
              </div>
            </Link>
          ))}
          {items.length === 0 && (
            <div className="text-center py-20 text-[#6b6b6b] border hairline">
              <p>No articles published yet.</p>
            </div>
          )}
        </div>
      )}
    </main>
  );
}

export function ArticleDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");
  usePageTitle(a?.title, a?.excerpt);
  useEffect(() => {
    client.get(`/articles/${slug}`).then(r => setA(r.data)).catch(() => setErr("Article not found"));
  }, [slug]);

  if (err) return <main className="py-32 text-center"><p>{err}</p></main>;
  if (!a) return <Loader label="Loading article" />;

  const paragraphs = a.body.split("\n\n");

  return (
    <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
      <Link to="/thought-leadership" className="link-underline text-[12px] tracking-[0.18em] uppercase mb-8 inline-block">← All Articles</Link>

      <article className="max-w-3xl mx-auto">
        <div className="mb-10">
          <div className="eyebrow text-[#b8975d] mb-4">{a.category}</div>
          <h1 className="serif-title text-4xl lg:text-6xl text-[#0a1628] mb-6 leading-[1.1]">{a.title}</h1>
          <div className="flex items-center gap-4 text-[13px] text-[#6b6b6b]">
            <span>By {a.author || "Nitin Narang"}</span>
            <span className="opacity-50">·</span>
            <span>{new Date(a.date).toLocaleDateString("en-IN", {month: "long", day: "numeric", year: "numeric"})}</span>
          </div>
        </div>

        <div className="rule mb-10"></div>

        <div className="prose-body">
          <p className="text-[20px] leading-[1.7] text-[#0a1628] mb-8 font-display italic">{a.excerpt}</p>
          {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
        </div>

        <div className="rule my-10"></div>

        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 border hairline flex items-center justify-center font-display text-xl">NN</div>
            <div>
              <div className="font-display text-lg text-[#0a1628]">{a.author || "Nitin Narang"}</div>
              <div className="text-[12px] tracking-[0.18em] uppercase text-[#6b6b6b]">President, AICF</div>
            </div>
          </div>
          <ShareButtons title={a.title} excerpt={a.excerpt} />
        </div>
      </article>
    </main>
  );
}
